import React, { useEffect, useState } from "react";
import { QrCode, ClipboardCheck, Percent, Eye, Loader2, ArrowUpRight, ArrowDownRight, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RANGES = [["today", "Aujourd'hui"], ["7d", "7 jours"], ["30d", "30 jours"], ["all", "Tout"]];

function Delta({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-xs font-semibold text-caneat-muted">— pas assez de données</span>;
  }
  const up = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${up ? "text-emerald-600" : "text-red-500"}`}>
      {up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
      {up ? "+" : ""}{value}% vs période préc.
    </span>
  );
}

function Kpi({ icon: Icon, label, value, delta, accent }) {
  return (
    <div className="rounded-2xl bg-white p-4 ring-1 ring-caneat-cream" data-testid={`kpi-${label}`}>
      <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" strokeWidth={2.4} />
      </div>
      <p className="mt-3 text-2xl font-extrabold text-caneat-ink sm:text-3xl">{value}</p>
      <p className="text-xs font-semibold text-caneat-muted sm:text-sm">{label}</p>
      <div className="mt-2"><Delta value={delta} /></div>
    </div>
  );
}

export default function DashboardHome({ onGoto }) {
  const { authHeaders, user } = useAuth();
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/admin/dashboard", { params: { range }, headers: authHeaders() })
      .then(({ data }) => { if (alive) { setData(data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range, authHeaders]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="caneat-title text-3xl text-caneat-ink">Bonjour, {user?.name?.split(" ")[0] || "Admin"} 👋</h1>
          <p className="text-sm font-medium text-caneat-muted">Vue d'ensemble de l'activité CAN'EAT</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {RANGES.map(([k, l]) => (
            <button key={k} onClick={() => setRange(k)} data-testid={`dash-range-${k}`}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold ${range === k ? "bg-caneat-orange text-white" : "bg-white text-caneat-muted ring-1 ring-caneat-cream"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="flex h-48 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-caneat-orange" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
            <Kpi icon={QrCode} label="Scans QR code" value={data.scans} delta={data.scans_delta} accent="bg-caneat-orange/15 text-caneat-orange" />
            <Kpi icon={ClipboardCheck} label="Sondages complétés" value={data.completed} delta={data.completed_delta} accent="bg-emerald-100 text-emerald-600" />
            <Kpi icon={Percent} label="Taux de réponse" value={`${data.response_rate}%`} delta={data.rate_delta} accent="bg-sky-100 text-sky-600" />
            <Kpi icon={Eye} label="Consultations produits" value={data.product_views} delta={data.product_views_delta} accent="bg-violet-100 text-violet-600" />
          </div>

          <div className="rounded-2xl bg-white p-5 ring-1 ring-caneat-cream" data-testid="recent-activity">
            <div className="mb-4 flex items-center gap-2">
              <Activity className="h-5 w-5 text-caneat-orange" />
              <h2 className="caneat-title text-xl text-caneat-ink">Activité récente</h2>
            </div>
            {(!data.recent_activity || data.recent_activity.length === 0) ? (
              <p className="py-6 text-center text-sm text-caneat-muted">Aucune action enregistrée pour le moment.</p>
            ) : (
              <ul className="divide-y divide-caneat-cream">
                {data.recent_activity.map((a) => (
                  <li key={a.id} className="flex items-start gap-3 py-2.5">
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-caneat-orange" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-caneat-ink">
                        {a.action}{a.detail ? <span className="font-normal text-caneat-muted"> — {a.detail}</span> : null}
                      </p>
                      <p className="text-xs text-caneat-muted">
                        {new Date(a.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · {a.admin_email}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
